import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Custom element for managing product add-on selections on the product page.
 *
 * Reads selected checkboxes/selects and returns a payload array of fee descriptors
 * that the product form attaches to the main line item on add-to-cart.
 *
 * Add-ons can be marked as required via the `data-required="true"` attribute on
 * their `.product-addons__item` container (driven by the `custom.addon_required`
 * product metafield). The product form calls `validate()` before submitting to
 * ensure all required add-ons have a selection.
 *
 * When a variant changes, the component morphs itself from the re-rendered
 * section HTML so that variant-level `custom.addons` metafields take effect.
 */
class ProductAddonsComponent extends HTMLElement {
  #abortController = new AbortController();

  /** @type {((event: MouseEvent) => void) | undefined} */
  #documentClickHandler;

  connectedCallback() {
    this.addEventListener('change', this.#onFieldChange);
    this.addEventListener('click', this.#onDropdownClick);
    this.addEventListener('keydown', this.#onDropdownKeydown);
    this.addEventListener('focusout', this.#onFocusOut);

    this.#documentClickHandler = (event) => {
      if (!this.contains(/** @type {Node} */ (event.target))) {
        this.#closeAllDropdowns();
      }
    };
    document.addEventListener('click', this.#documentClickHandler);

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onFieldChange);
    this.removeEventListener('click', this.#onDropdownClick);
    this.removeEventListener('keydown', this.#onDropdownKeydown);
    if (this.#documentClickHandler) {
      document.removeEventListener('click', this.#documentClickHandler);
    }
    this.#abortController.abort();
  }

  /**
   * Returns the add-on payload for the currently selected add-ons.
   * The product form calls this at submit time and attaches the result
   * as the `_addon_rule_payload` line item property.
   *
   * @param {string|number} productId - The main product ID
   * @param {string|number} variantId - The currently selected variant ID
   * @returns {Array<{rule_code: string, fee_variant_id: number, fee_price: number, scope: string, addon_key: string}>}
   */
  getAddonPayload(productId, variantId) {
    /** @type {Array<{rule_code: string, fee_variant_id: number, fee_price: number, scope: string, addon_key: string}>} */
    const payload = [];

    // Checked checkboxes (single-variant addons)
    this.querySelectorAll('.product-addons__checkbox:checked').forEach((el) => {
      const checkbox = /** @type {HTMLInputElement} */ (el);
      const ruleCode = checkbox.dataset.ruleCode || '';
      const feeVariantId = parseInt(checkbox.dataset.feeVariantId || '0', 10);
      const feePrice = parseInt(checkbox.dataset.feePrice || '0', 10);
      const scope = checkbox.dataset.scope || 'per_order';
      const addonKey = this.#computeKey(ruleCode, scope, productId, variantId, feeVariantId);

      payload.push({ rule_code: ruleCode, fee_variant_id: feeVariantId, fee_price: feePrice, scope: scope, addon_key: addonKey });
    });

    // Selects with a value chosen (multi-variant addons)
    this.querySelectorAll('.product-addons__select').forEach((el) => {
      const select = /** @type {HTMLSelectElement} */ (el);
      if (!select.value) return;

      const ruleCode = select.dataset.ruleCode || '';
      const scope = select.dataset.scope || 'per_order';
      const selectedOption = /** @type {HTMLOptionElement} */ (select.options[select.selectedIndex]);
      const feeVariantId = parseInt(selectedOption.dataset.feeVariantId || '0', 10);
      const feePrice = parseInt(selectedOption.dataset.feePrice || '0', 10);
      const addonKey = this.#computeKey(ruleCode, scope, productId, variantId, feeVariantId);

      payload.push({ rule_code: ruleCode, fee_variant_id: feeVariantId, fee_price: feePrice, scope: scope, addon_key: addonKey });
    });

    return payload;
  }

  /**
   * Validates that all required add-ons have a selection.
   * Toggles error styling and shows/hides inline error messages.
   *
   * @returns {boolean} true when every required add-on is satisfied
   */
  validate() {
    let isValid = true;

    this.querySelectorAll('.product-addons__item[data-required="true"]').forEach((el) => {
      const item = /** @type {HTMLElement} */ (el);
      const checkbox = /** @type {HTMLInputElement|null} */ (item.querySelector('.product-addons__checkbox'));
      const select = /** @type {HTMLSelectElement|null} */ (item.querySelector('.product-addons__select'));
      const errorEl = /** @type {HTMLElement|null} */ (item.querySelector('.product-addons__error'));

      let satisfied = false;

      if (checkbox) {
        satisfied = checkbox.checked;
      } else if (select) {
        satisfied = select.value !== '';
      }

      if (!satisfied) {
        isValid = false;
        item.classList.add('product-addons__item--error');
        if (errorEl) errorEl.hidden = false;
      } else {
        item.classList.remove('product-addons__item--error');
        if (errorEl) errorEl.hidden = true;
      }
    });

    return isValid;
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  /**
   * Handles clicks on custom dropdown triggers and options.
   * @param {MouseEvent} event
   */
  #onDropdownClick = (event) => {
    const trigger = /** @type {HTMLElement} */ (event.target)?.closest('.product-addons__select-trigger');
    const option = /** @type {HTMLElement} */ (event.target)?.closest('.product-addons__select-option');

    if (trigger) {
      const wrapper = trigger.closest('[data-addon-custom-select]');
      if (!wrapper) return;
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      this.#closeAllDropdowns();
      if (!isOpen) {
        const panel = wrapper.querySelector('.product-addons__select-panel');
        if (panel) {
          panel.removeAttribute('hidden');
          trigger.setAttribute('aria-expanded', 'true');
        }
      }
      return;
    }

    if (option) {
      const radio = /** @type {HTMLInputElement|null} */ (option.querySelector('input[type="radio"]'));
      if (!radio) return;

      radio.checked = true;

      const wrapper = option.closest('[data-addon-custom-select]');
      if (!wrapper) return;

      // Sync hidden native select
      const select = /** @type {HTMLSelectElement|null} */ (wrapper.querySelector('.product-addons__select'));
      if (select) {
        select.value = radio.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Update trigger label (including color swatch when available)
      const currentLabel = wrapper.querySelector('.product-addons__select-current');
      if (currentLabel) {
        const title = radio.dataset.title ?? radio.dataset.label ?? radio.value;
        const price = radio.dataset.price;
        const swatchColor = radio.dataset.swatchColor;
        const swatchImage = radio.dataset.swatchImage;

        let swatchHtml = '';
        if (swatchColor) {
          swatchHtml = `<span class="product-addons__color-swatch" style="background-color: ${swatchColor}"></span>`;
        } else if (swatchImage) {
          swatchHtml = `<span class="product-addons__color-swatch" style="background-image: url(${swatchImage}); background-size: cover;"></span>`;
        }

        if (price) {
          currentLabel.innerHTML = `${swatchHtml}<span class="product-addons__select-current-text">${title}</span><span class="product-addons__select-current-price">+${price}</span>`;
        } else {
          currentLabel.innerHTML = `${swatchHtml}<span class="product-addons__select-current-text">${title}</span>`;
        }
      }

      this.#closeAllDropdowns();
    }
  };

  /**
   * Closes open dropdowns when focus leaves this component entirely.
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    if (event.relatedTarget && !this.contains(/** @type {Node} */ (event.relatedTarget))) {
      this.#closeAllDropdowns();
    }
  };

  /**
   * Full keyboard navigation for custom addon dropdowns.
   *
   * On the trigger:
   *   Enter / Space / ArrowDown — open (if closed) and focus selected-or-first option
   *   ArrowUp                   — open (if closed) and focus last option
   *   Escape                    — close
   *
   * On an option:
   *   ArrowDown / ArrowUp  — move between options (wraps)
   *   Home / End           — jump to first / last option
   *   Enter / Space        — select option, return focus to trigger
   *   Escape               — close, return focus to trigger
   *   Tab                  — close, let browser move focus naturally
   *
   * @param {KeyboardEvent} event
   */
  #onDropdownKeydown = (event) => {
    const trigger = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (event.target)?.closest('.product-addons__select-trigger')
    );
    const option = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (event.target)?.closest('.product-addons__select-option')
    );
    const wrapper = /** @type {HTMLElement | null} */ ((trigger ?? option)?.closest('[data-addon-custom-select]'));

    if (!wrapper) {
      if (event.key === 'Escape') this.#closeAllDropdowns();
      return;
    }

    const panel = /** @type {HTMLElement | null} */ (wrapper.querySelector('.product-addons__select-panel'));

    if (trigger) {
      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          if (panel?.hasAttribute('hidden')) {
            panel.removeAttribute('hidden');
            trigger.setAttribute('aria-expanded', 'true');
          }
          this.#focusDropdownOption(wrapper, 'selected-or-first');
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          if (panel?.hasAttribute('hidden')) {
            panel.removeAttribute('hidden');
            trigger.setAttribute('aria-expanded', 'true');
          }
          this.#focusDropdownOption(wrapper, 'last');
          break;
        }
        case 'Enter':
        case ' ': {
          event.preventDefault();
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (isOpen) {
            this.#closeAllDropdowns();
          } else {
            panel?.removeAttribute('hidden');
            trigger.setAttribute('aria-expanded', 'true');
            this.#focusDropdownOption(wrapper, 'selected-or-first');
          }
          break;
        }
        case 'Escape':
          this.#closeAllDropdowns();
          break;
      }
      return;
    }

    if (option) {
      const options = /** @type {HTMLElement[]} */ (
        Array.from(wrapper.querySelectorAll('.product-addons__select-option'))
      );
      const currentIndex = options.indexOf(option);

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          options[(currentIndex + 1) % options.length]?.focus();
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          options[(currentIndex - 1 + options.length) % options.length]?.focus();
          break;
        }
        case 'Home':
          event.preventDefault();
          options[0]?.focus();
          break;
        case 'End':
          event.preventDefault();
          options[options.length - 1]?.focus();
          break;
        case 'Enter':
        case ' ': {
          event.preventDefault();
          const focusTrigger = /** @type {HTMLElement | null} */ (
            wrapper.querySelector('.product-addons__select-trigger')
          );
          option.click();
          focusTrigger?.focus();
          break;
        }
        case 'Escape': {
          const focusTrigger = /** @type {HTMLElement | null} */ (
            wrapper.querySelector('.product-addons__select-trigger')
          );
          this.#closeAllDropdowns();
          focusTrigger?.focus();
          break;
        }
        case 'Tab':
          this.#closeAllDropdowns();
          break;
      }
    }
  };

  /**
   * Moves focus to an option inside an open dropdown panel.
   * @param {HTMLElement} wrapper - The [data-addon-custom-select] wrapper element.
   * @param {'selected-or-first' | 'last'} [which='selected-or-first'] - Which option to focus.
   */
  #focusDropdownOption(wrapper, which = 'selected-or-first') {
    const options = /** @type {HTMLElement[]} */ (
      Array.from(wrapper.querySelectorAll('.product-addons__select-option'))
    );
    if (!options.length) return;

    if (which === 'last') {
      options[options.length - 1].focus();
      return;
    }

    const selected = options.find((el) => el.querySelector('input:checked'));
    (selected ?? options[0]).focus();
  }

  /**
   * Closes all open custom dropdown panels inside this component.
   */
  #closeAllDropdowns() {
    this.querySelectorAll('.product-addons__select-panel').forEach((panel) => {
      panel.setAttribute('hidden', '');
    });
    this.querySelectorAll('.product-addons__select-trigger').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * Morphs this component from the re-rendered section HTML when the
   * selected variant changes, so variant-level addons are displayed.
   *
   * Preserves user-made addon selections (checked checkboxes, chosen
   * select values, and custom dropdown trigger labels) across the morph
   * so that changing a product variant doesn't reset addon choices.
   *
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const newDoc = event.detail.data.html;
    if (!newDoc) return;

    const newAddons = newDoc.querySelector('product-addons-component');
    if (!newAddons) return;

    const savedSelections = this.#captureSelections();

    morph(this, newAddons);
    this.hidden = newAddons.hidden;

    this.#restoreSelections(savedSelections);
  };

  /**
   * Captures the current user-made addon selections so they can be
   * restored after a DOM morph.
   *
   * @returns {Map<string, {type: 'checkbox', checked: boolean} | {type: 'select', value: string, triggerHTML: string}>}
   */
  #captureSelections() {
    /** @type {Map<string, {type: 'checkbox', checked: boolean} | {type: 'select', value: string, triggerHTML: string}>} */
    const selections = new Map();

    this.querySelectorAll('.product-addons__item').forEach((el) => {
      const item = /** @type {HTMLElement} */ (el);
      const handle = item.dataset.addonHandle;
      if (!handle) return;

      const checkbox = /** @type {HTMLInputElement|null} */ (item.querySelector('.product-addons__checkbox'));
      if (checkbox) {
        selections.set(handle, { type: 'checkbox', checked: checkbox.checked });
        return;
      }

      const select = /** @type {HTMLSelectElement|null} */ (item.querySelector('.product-addons__select'));
      if (select && select.value) {
        const currentLabel = item.querySelector('.product-addons__select-current');
        selections.set(handle, {
          type: 'select',
          value: select.value,
          triggerHTML: currentLabel ? currentLabel.innerHTML : '',
        });
      }
    });

    return selections;
  }

  /**
   * Restores previously captured addon selections after a DOM morph.
   *
   * @param {Map<string, {type: 'checkbox', checked: boolean} | {type: 'select', value: string, triggerHTML: string}>} selections
   */
  #restoreSelections(selections) {
    if (selections.size === 0) return;

    this.querySelectorAll('.product-addons__item').forEach((el) => {
      const item = /** @type {HTMLElement} */ (el);
      const handle = item.dataset.addonHandle;
      if (!handle) return;

      const saved = selections.get(handle);
      if (!saved) return;

      if (saved.type === 'checkbox') {
        const checkbox = /** @type {HTMLInputElement|null} */ (item.querySelector('.product-addons__checkbox'));
        if (checkbox) checkbox.checked = saved.checked;
      } else if (saved.type === 'select') {
        const select = /** @type {HTMLSelectElement|null} */ (item.querySelector('.product-addons__select'));
        if (select) {
          const optionExists = Array.from(select.options).some((o) => o.value === saved.value);
          if (optionExists) {
            select.value = saved.value;

            const radio = /** @type {HTMLInputElement|null} */ (
              item.querySelector(`input[type="radio"][value="${saved.value}"]`)
            );
            if (radio) radio.checked = true;

            if (saved.triggerHTML) {
              const currentLabel = item.querySelector('.product-addons__select-current');
              if (currentLabel) currentLabel.innerHTML = saved.triggerHTML;
            }
          }
        }
      }
    });
  }

  /**
   * Clears the error state on the addon item that just changed.
   *
   * @param {Event} event
   */
  #onFieldChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const item = /** @type {HTMLElement|null} */ (target.closest('.product-addons__item[data-required="true"]'));
    if (!item) return;

    item.classList.remove('product-addons__item--error');
    const errorEl = /** @type {HTMLElement|null} */ (item.querySelector('.product-addons__error'));
    if (errorEl) errorEl.hidden = true;
  };

  /**
   * Computes a deterministic key for a fee based on rule, scope and parent IDs.
   *
   * @param {string} ruleCode
   * @param {string} scope
   * @param {string|number} productId
   * @param {string|number} variantId
   * @param {number} feeVariantId
   * @returns {string}
   */
  #computeKey(ruleCode, scope, productId, variantId, feeVariantId) {
    switch (scope) {
      case 'per_product':
        return `${ruleCode}::per_product::${productId}`;
      case 'per_variant':
        return `${ruleCode}::per_variant::${variantId}`;
      case 'per_unique_addon':
        return `${ruleCode}::per_unique_addon::${feeVariantId}`;
      case 'per_order':
      default:
        return `${ruleCode}::per_order`;
    }
  }
}

if (!customElements.get('product-addons-component')) {
  customElements.define('product-addons-component', ProductAddonsComponent);
}
